import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { GenericEntityBlock } from '@lumapps/lumx-blocks/components/GenericEntityBlock';
import { mdiNewspaper } from '@lumapps/lumx/icons';
import { Size } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { formatFromNow } from '@lumapps/utils/date/formatFromNow';

import { Article, CommunityNavArticle } from '../../types';

export interface ArticleBlockProps {
    article: CommunityNavArticle | Partial<Article>;
    isDisabled?: boolean;
    variant?: string;
    context?: 'platform' | 'site' | 'community';
}

export const CLASSNAME = 'article-block';

/**
 * Component used for displaying articles as a block
 *
 * @family Blocks
 * @param ArticleBlockProps
 * @returns ArticleBlock
 */
export const ArticleBlock = React.forwardRef<HTMLDivElement, ArticleBlockProps>((props, ref) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { article, isDisabled, variant, context } = props;

    const { structuredContent, createdAt, updatedAt, author, title, communityReference, site } =
        article as Partial<Article>;

    const { translateKey, translateObject } = useTranslate();
    const translatedTitle = translateObject(title) as string;

    const titleToDisplay = structuredContent?.title?.value || translatedTitle;
    const intro = structuredContent?.intro?.value;
    const featuredImage = structuredContent?.featuredImage;
    const featureImageTranslations = featuredImage?.translations;
    const communityName = communityReference?.name.value;
    const siteName = site?.name;
    const isSmallVariant = variant === 'small';

    const parentInformation = React.useMemo(() => {
        if (context === 'platform') {
            return `${translateKey(GLOBAL.IN)} ${siteName} > ${communityName}`;
        }
        if (context === 'site') {
            return `${translateKey(GLOBAL.IN)} ${communityName}`;
        }
        return null;
    }, [communityName, context, siteName, translateKey]);

    const lastUpdateDate = updatedAt || createdAt;

    return (
        <GenericEntityBlock
            ref={ref}
            className={block()}
            title={titleToDisplay}
            thumbnail={{
                icon: mdiNewspaper,
                image: featureImageTranslations
                    ? featureImageTranslations[featuredImage.lang as string].url
                    : undefined,
                size: isSmallVariant ? Size.m : Size.l,
            }}
            metadata={
                isSmallVariant || !parentInformation ? (
                    <GenericEntityBlock.Attribute key="intro" value={intro} />
                ) : (
                    <GenericEntityBlock.Attribute key="parent-information" value={parentInformation} />
                )
            }
            additionalInfo={
                !isSmallVariant
                    ? [
                          author && <GenericEntityBlock.User key="author" className={element('author')} {...author} />,
                          <GenericEntityBlock.Attribute
                              key="lastUpdate"
                              className={element('last-update-date')}
                              value={formatFromNow({
                                  date: lastUpdateDate,
                                  thresholdDisplayFullDate: 2,
                                  dateStringFormat: 'LL',
                              })}
                          />,
                      ]
                    : undefined
            }
            isDisabled={isDisabled}
        />
    );
});
